/**
 * LeftShield Portal - Board View Page
 * Executive-focused board report with compliance gauges,
 * per-framework traffic lights, and summary statistics.
 *
 * Route: /board | Nav: Reports > Board View
 */
import React from 'react';
import {
  Title1,
  Title2,
  Body1,
  Body2,
  Card,
  CardHeader,
  Badge,
  Button,
  Spinner,
  ProgressBar,
  tokens,
} from '@fluentui/react-components';
import {
  BoardRegular,
  ArrowDownloadRegular,
  CheckmarkCircleRegular,
  DismissCircleRegular,
  ShieldCheckmarkRegular,
} from '@fluentui/react-icons';
import { useQuery } from '@tanstack/react-query';
import { dashboardApi, complianceApi, governanceApi } from '../services/api';
import { usePageStyles } from '../styles/page-styles';
import { PageHeader } from '../components/common/PageHeader';
import { ComplianceCoverageRadar } from '../components/compliance/ComplianceCoverageRadar';
import { ConfidenceRing } from '../components/confidence';
import type {
  ComplianceDashboard,
  DashboardOverview,
  ComplianceFramework,
  BoardPosture,
} from '../types';

const getTrafficLightColor = (score: number): 'success' | 'warning' | 'danger' => {
  if (score >= 80) return 'success';
  if (score >= 50) return 'warning';
  return 'danger';
};

const getTrafficLightLabel = (score: number): string => {
  if (score >= 80) return 'On Track';
  if (score >= 50) return 'Risk Exposure';
  return 'Critical';
};

const getScoreColor = (score: number): string => {
  if (score >= 80) return tokens.colorPaletteGreenForeground1;
  if (score >= 50) return tokens.colorPaletteYellowForeground1;
  return tokens.colorPaletteRedForeground1;
};

export const BoardViewPage: React.FC = () => {
  const ps = usePageStyles();

  const { data: complianceDashboard, isLoading: complianceLoading } = useQuery<ComplianceDashboard>({
    queryKey: ['boardView', 'compliance'],
    queryFn: () => dashboardApi.getComplianceDashboard(),
  });

  const { data: overview, isLoading: overviewLoading } = useQuery<DashboardOverview>({
    queryKey: ['boardView', 'overview'],
    queryFn: () => dashboardApi.getDashboardOverview(),
  });

  const { data: frameworks, isLoading: frameworksLoading } = useQuery<ComplianceFramework[]>({
    queryKey: ['boardView', 'frameworks'],
    queryFn: () => complianceApi.getFrameworks(),
  });

  const { data: boardPosture } = useQuery<BoardPosture>({
    queryKey: ['boardView', 'posture'],
    queryFn: () => governanceApi.getBoardPosture(30),
  });

  const isLoading = complianceLoading || overviewLoading || frameworksLoading;

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalXXXL }}>
        <Spinner size="large" label="Loading board report..." />
      </div>
    );
  }

  const governanceScore = overview?.postureScore ?? 0;
  const overallScore = complianceDashboard?.overallScore ?? 0;
  const totalFrameworks = frameworks?.length ?? 0;
  const averageScore = totalFrameworks > 0
    ? Math.round((frameworks?.reduce((sum, f) => sum + (f.complianceScore ?? 0), 0) ?? 0) / totalFrameworks)
    : 0;
  const criticalFindings = complianceDashboard?.criticalFindings ?? 0;
  const allZero = governanceScore === 0 && overallScore === 0 && totalFrameworks === 0;

  const handleExport = () => {
    alert('Report export will be available in a future release. Use the Evidence page for current export options.');
  };

  return (
    <div className={ps.page}>
      <PageHeader
        title="Board View"
        subtitle="What does the board need to know? Executive summary of governance posture for board reporting."
        actions={
          <Button
            appearance="primary"
            icon={<ArrowDownloadRegular />}
            onClick={handleExport}
          >
            Export Report
          </Button>
        }
      />

      {/* Governance Score Gauge — headline metric */}
      <Card>
        <CardHeader
          image={
            <ShieldCheckmarkRegular
              style={{ fontSize: '2rem', color: allZero ? tokens.colorNeutralForeground3 : getScoreColor(governanceScore) }}
            />
          }
          header={<Title2>Governance Score</Title2>}
          description={
            <Body2 title="Composite metric combining compliance coverage, enforcement rate, evidence confidence, and risk exposure into a single governance health indicator">
              Overall organizational security maturity — hover for details
            </Body2>
          }
        />
        <div className={ps.cardBody}>
          {allZero ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXL }}>
              <Body1 style={{ color: tokens.colorNeutralForeground3 }}>Not yet activated — enable frameworks to generate scores</Body1>
              <Badge appearance="tint" color="informative">Pending</Badge>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXL }}>
              <Title1 className="metric-value" style={{ fontSize: '3rem', color: getScoreColor(governanceScore) }}>
                {governanceScore}
              </Title1>
              <div style={{ flex: 1 }}>
                <ProgressBar
                  value={governanceScore / 100}
                  thickness="large"
                />
              </div>
              <ConfidenceRing score={78} size={52} />
              <Badge
                appearance="filled"
                color={getTrafficLightColor(governanceScore)}
                style={{ fontSize: tokens.fontSizeBase300, padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}` }}
              >
                {getTrafficLightLabel(governanceScore)}
              </Badge>
            </div>
          )}
        </div>
      </Card>

      {/* Board Narrative */}
      {boardPosture?.boardNarrative && (
        <Card>
          <CardHeader
            header={<Title2>Board Narrative</Title2>}
            description="Auto-generated governance summary for board reporting"
          />
          <div className={ps.cardBody}>
            <Body1>{boardPosture.boardNarrative}</Body1>
            {boardPosture.exceptionPressureBand && (
              <div style={{ display: 'flex', gap: tokens.spacingHorizontalM, marginTop: tokens.spacingVerticalM }}>
                <Badge appearance="filled" color={boardPosture.exceptionPressureBand === 'Healthy' ? 'success' : boardPosture.exceptionPressureBand === 'Watch' ? 'warning' : 'danger'}>
                  Exception Pressure: {boardPosture.exceptionPressureBand}
                </Badge>
                <Badge appearance="outline">
                  Runtime Coverage: {boardPosture.runtimeCoverage?.coveragePct?.toFixed(1)}%
                </Badge>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Summary Statistics */}
      <div className={ps.metricGrid}>
        <Card>
          <CardHeader
            image={<BoardRegular style={{ fontSize: '1.5rem', color: allZero ? tokens.colorNeutralForeground3 : tokens.colorBrandForeground1 }} />}
            header={<Body2>Total Frameworks</Body2>}
            description={
              <Title2 className="metric-value" style={{ color: allZero ? tokens.colorNeutralForeground4 : undefined }}>
                {totalFrameworks}
              </Title2>
            }
          />
        </Card>
        <Card>
          <CardHeader
            image={<CheckmarkCircleRegular style={{ fontSize: '1.5rem', color: allZero ? tokens.colorNeutralForeground3 : tokens.colorPaletteGreenForeground1 }} />}
            header={<Body2>Average Score</Body2>}
            description={
              <Title2 className="metric-value" style={{ color: allZero ? tokens.colorNeutralForeground4 : getScoreColor(averageScore) }}>
                {allZero ? 'Awaiting data' : `${averageScore}%`}
              </Title2>
            }
          />
        </Card>
        <Card>
          <CardHeader
            image={<DismissCircleRegular style={{ fontSize: '1.5rem', color: criticalFindings > 0 ? tokens.colorPaletteRedForeground1 : tokens.colorNeutralForeground3 }} />}
            header={<Body2>Critical Findings</Body2>}
            description={
              <Title2 className="metric-value" style={{ color: criticalFindings > 0 ? tokens.colorPaletteRedForeground1 : tokens.colorNeutralForeground4 }}>
                {criticalFindings}
              </Title2>
            }
          />
        </Card>
        <Card>
          <CardHeader
            image={<ShieldCheckmarkRegular style={{ fontSize: '1.5rem', color: allZero ? tokens.colorNeutralForeground3 : tokens.colorBrandForeground1 }} />}
            header={<Body2>Compliance Score</Body2>}
            description={
              <Title2 className="metric-value" style={{ color: allZero ? tokens.colorNeutralForeground4 : getScoreColor(overallScore) }}>
                {allZero ? 'Awaiting data' : `${overallScore}%`}
              </Title2>
            }
          />
        </Card>
      </div>

      {/* Trend Since Last Board Meeting */}
      {!allZero && (
        <Card>
          <CardHeader
            header={<Title2>90-Day Governance Trajectory</Title2>}
            description="Trend indicators since the last board reporting period"
          />
          <div className={ps.cardBody}>
            <div className={ps.statGrid}>
              <div className={ps.statTile}>
                <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Governance Score</Body2>
                <Title2 style={{ color: getScoreColor(governanceScore) }}>{governanceScore}</Title2>
                <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                  {complianceDashboard?.scoreChange24h != null
                    ? `${complianceDashboard.scoreChange24h >= 0 ? '+' : ''}${complianceDashboard.scoreChange24h} pts (24h)`
                    : 'Trend data collecting'}
                </Body2>
              </div>
              <div className={ps.statTile}>
                <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Compliance Score</Body2>
                <Title2 style={{ color: getScoreColor(overallScore) }}>{overallScore}%</Title2>
                <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Target: 90%</Body2>
              </div>
              <div className={ps.statTile}>
                <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Frameworks Active</Body2>
                <Title2>{complianceDashboard?.enabledFrameworks ?? 0} / {totalFrameworks}</Title2>
              </div>
              <div className={ps.statTile}>
                <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Critical Findings</Body2>
                <Title2 style={{ color: criticalFindings > 0 ? tokens.colorPaletteRedForeground1 : tokens.colorPaletteGreenForeground1 }}>
                  {criticalFindings}
                </Title2>
                <Body2 style={{ color: tokens.colorNeutralForeground3 }}>
                  {criticalFindings === 0 ? 'Clean' : 'Requires attention'}
                </Body2>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Top Governance Risks */}
      {overview?.topGaps && overview.topGaps.length > 0 && (
        <Card>
          <CardHeader
            header={<Title2>Top Governance Risks</Title2>}
            description="Priority governance gaps requiring board awareness"
          />
          <div className={ps.cardBody}>
            <div className={ps.rowList}>
              {overview.topGaps.slice(0, 5).map((gap, idx) => (
                <div key={idx} className={ps.rowItem}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, flex: 1 }}>
                    <Badge
                      appearance="filled"
                      color={gap.severity === 'Critical' ? 'danger' : gap.severity === 'High' ? 'important' : 'warning'}
                      size="small"
                    >
                      {gap.severity}
                    </Badge>
                    <Body1 style={{ fontWeight: 500 }}>{gap.description}</Body1>
                  </div>
                  <Body2 style={{ color: tokens.colorNeutralForeground3 }}>{gap.recommendedAction}</Body2>
                </div>
              ))}
            </div>
          </div>
        </Card>
      )}

      {/* Compliance Coverage Radar */}
      <Card>
        <CardHeader header={<Title2>Compliance Coverage</Title2>} />
        <div className={ps.cardBody}>
          <ComplianceCoverageRadar />
        </div>
      </Card>

      {/* Per-Framework Cards Grid */}
      <div className={ps.sectionHeader}>
        <Title2>Framework Status</Title2>
      </div>
      {frameworks && frameworks.length > 0 ? (
        <div className={ps.metricGrid}>
          {frameworks.map((framework) => {
            const score = framework.complianceScore ?? 0;
            return (
              <Card key={framework.id}>
                <CardHeader
                  header={<Body1 style={{ fontWeight: 600 }}>{framework.name}</Body1>}
                  description={
                    <Badge appearance="outline">{framework.code} v{framework.version}</Badge>
                  }
                  action={
                    <Badge
                      appearance="filled"
                      color={getTrafficLightColor(score)}
                    >
                      {getTrafficLightLabel(score)}
                    </Badge>
                  }
                />
                <div className={ps.cardBody}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM }}>
                    <ProgressBar
                      value={score / 100}
                      thickness="large"
                      style={{ flex: 1 }}
                    />
                    <Body1 style={{ fontWeight: 600, color: getScoreColor(score) }}>
                      {score}%
                    </Body1>
                  </div>
                  <div className={ps.statGrid}>
                    <div className={ps.statTile}>
                      <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Passed</Body2>
                      <Body1 style={{ fontWeight: 600, color: tokens.colorPaletteGreenForeground1 }}>
                        {framework.passedCount}
                      </Body1>
                    </div>
                    <div className={ps.statTile}>
                      <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Failed</Body2>
                      <Body1 style={{ fontWeight: 600, color: tokens.colorPaletteRedForeground1 }}>
                        {framework.failedCount}
                      </Body1>
                    </div>
                    <div className={ps.statTile}>
                      <Body2 style={{ color: tokens.colorNeutralForeground3 }}>Controls</Body2>
                      <Body1 style={{ fontWeight: 600 }}>{framework.controlCount}</Body1>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: `${tokens.spacingVerticalXXXL} ${tokens.spacingHorizontalXXL}`,
              gap: tokens.spacingVerticalM,
              textAlign: 'center',
            }}
          >
            <BoardRegular style={{ fontSize: '3rem', color: tokens.colorNeutralForeground3 }} />
            <Body1 style={{ fontWeight: 600 }}>Frameworks ready for activation</Body1>
            <Body2 style={{ color: tokens.colorNeutralForeground3, maxWidth: '420px' }}>
              Enable compliance frameworks to populate your board report with scores and status.
            </Body2>
            <Button appearance="primary" as="a" href="/frameworks" style={{ marginTop: tokens.spacingVerticalS }}>
              Enable Frameworks
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
};
